import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  formatDate,
  formatUSAmount,
} from "../../utils/helperFunctions/helperFunctions";

const MakePaymentModal = ({
  setShowPaymentModal,
  handleSubmitPayment,
  setCardDetails,
  setShowPopup,
}) => {
  const [cardFirstName, setCardFirstName] = useState("");
  const [cardLastName, setCardLastName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvc, setCvc] = useState("");
  const [errors, setErrors] = useState({});
  const bookingDetails = useSelector((state) => state.bookingValidation.data);
  const [PayAmount, setPayAmount] = useState(
    bookingDetails.payment_details?.due_amount
  );

  const validate = () => {
    let errors = {};

    if (!cardFirstName) {
      errors.cardName = "Cardholder name is required";
    }

    if (!cardLastName) {
      errors.cardName = "Cardholder name is required";
    }

    if (!cardNumber) {
      errors.cardNumber = "Card number is required";
    } else if (!/^\d{16}$/.test(cardNumber)) {
      errors.cardNumber = "Card number must be 16 digits";
    }

    if (!expiryDate) {
      errors.expiryDate = "Expiry date is required";
    } else if (!/^(0[1-9]|1[0-2])\/?([0-9]{2})$/.test(expiryDate)) {
      errors.expiryDate = "Expiry date must be in MM/YY format";
    }

    if (!cvc) {
      errors.cvc = "CVC is required";
    } else if (!/^\d{3,4}$/.test(cvc)) {
      errors.cvc = "CVC must be 3 or 4 digits";
    }

    if (!PayAmount) {
      errors.payAmount = "Payment amount is required";
    } else if (isNaN(PayAmount)) {
      errors.payAmount = "Payment amount must be a number";
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      handleSubmitPayment();
    }
  };

  useEffect(() => {
    setCardDetails({
      first_name: cardFirstName,
      last_name: cardLastName,
      card_number: cardNumber,
      cvv_number: cvc,
      exp_month: expiryDate.split("/")[0],
      exp_year: expiryDate.split("/")[1],
      pay_amount: PayAmount,
    });
  }, [cardFirstName, cardLastName, cardNumber, expiryDate, cvc, PayAmount, setCardDetails]);

  const handleClose = () => {
    setShowPaymentModal(false);
    if (window.location.search) {
      window.history.pushState({}, document.title, window.location.pathname);
    }
  };

  return (
    <div className="relative z-50">
      <div
        onClick={handleClose}
        className="fixed hover:cursor-pointer inset-0 bg-black bg-opacity-50"
      ></div>
      <div className="w-fit h-fit fixed top-0 left-0 bottom-0 right-0 m-auto p-6 bg-white shadow-md rounded-lg">
        <button className="absolute top-0 right-0 p-4" onClick={handleClose}>
          <svg
            className="h-6 w-6 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <h2 className="text-2xl font-bold mb-6 text-center">Payment Details</h2>
        <div className="flex flex-col md:flex-row gap-10">
          {window.location.search.includes("setShowPaymentModal=true") && (
            <table className="text-mv-darkblue rounded-md bg-sky-200">
              <tbody>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left font-bold text-gray-700">
                    Booking ID
                  </th>
                  <td className="px-4 py-2"> {bookingDetails.booking_id}</td>
                </tr>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left font-bold text-gray-700">
                    Guest Name
                  </th>
                  <td className="px-4 py-2">
                    {bookingDetails.guest_details[0].first_name +
                      " " +
                      bookingDetails.guest_details[0].last_name}
                  </td>
                </tr>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left font-bold text-gray-700">
                    Itinerary
                  </th>
                  <td className="px-4 py-2">
                    {bookingDetails.itinerary_title}
                  </td>
                </tr>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left font-bold text-gray-700">
                    Sailing Date
                  </th>
                  <td className="px-4 py-2">
                    {formatDate(bookingDetails.sailing_date)}
                  </td>
                </tr>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left font-bold text-gray-700">
                    Ship
                  </th>
                  <td className="px-4 py-2">{bookingDetails.cruise_name}</td>
                </tr>
                <tr>
                  <th className="px-4 py-2 text-left font-bold text-gray-700">
                    Balance Remaining
                  </th>
                  <td className="px-4 py-2">
                    $
                    {formatUSAmount(bookingDetails.payment_details?.due_amount)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          <form className="" onSubmit={handleSubmit}>
            <div className="flex gap-5">
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="cardName"
                >
                  Cardholder First Name
                </label>
                <input
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                    errors.cardName && "border-red-500"
                  }`}
                  id="cardName"
                  type="text"
                  value={cardFirstName}
                  onChange={(e) => setCardFirstName(e.target.value)}
                  placeholder="John Doe"
                />
                {errors.cardName && (
                  <p className="text-red-500 text-xs italic">
                    {errors.cardName}
                  </p>
                )}
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="cardName"
                >
                  Cardholder Last Name
                </label>
                <input
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                    errors.cardName && "border-red-500"
                  }`}
                  id="cardName"
                  type="text"
                  value={cardLastName}
                  onChange={(e) => setCardLastName(e.target.value)}
                  placeholder="John Doe"
                />
                {errors.cardName && (
                  <p className="text-red-500 text-xs italic">
                    {errors.cardName}
                  </p>
                )}
              </div>
            </div>
            <div className="mb-4">
              <label
                className="block text-gray-700 text-sm font-bold mb-2"
                htmlFor="cardNumber"
              >
                Card Number
              </label>
              <input
                className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                  errors.cardNumber && "border-red-500"
                }`}
                id="cardNumber"
                type="text"
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                placeholder="1234 5678 9012 3456"
              />
              {errors.cardNumber && (
                <p className="text-red-500 text-xs italic">
                  {errors.cardNumber}
                </p>
              )}
            </div>
            <div className="flex mb-4">
              <div className="w-1/2 mr-2">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="expiryDate"
                >
                  Expiry Date
                </label>
                <input
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                    errors.expiryDate && "border-red-500"
                  }`}
                  id="expiryDate"
                  type="text"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  placeholder="MM/YY"
                />
                {errors.expiryDate && (
                  <p className="text-red-500 text-xs italic">
                    {errors.expiryDate}
                  </p>
                )}
              </div>
              <div className="w-1/2 ml-2">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="cvc"
                >
                  CVC
                </label>
                <input
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                    errors.cvc && "border-red-500"
                  }`}
                  id="cvc"
                  type="text"
                  value={cvc}
                  maxLength={3}
                  onChange={(e) => setCvc(e.target.value)}
                  placeholder="123"
                />
                {errors.cvc && (
                  <p className="text-red-500 text-xs italic">{errors.cvc}</p>
                )}
              </div>
            </div>
            {window.location.search.includes("setShowPaymentModal=true") && (
              <div>
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="payAmount"
                >
                  PAYMENT AMOUNT
                </label>
                <input
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                    errors.payAmount && "border-red-500"
                  }`}
                  id="payAmount"
                  type="text"
                  value={PayAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder="$1000"
                />
                {errors.payAmount && (
                  <p className="text-red-500 text-xs italic">
                    {errors.payAmount}
                  </p>
                )}
              </div>
            )}
            <div className="flex items-center py-5 justify-between">
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                type="submit"
              >
                Submit Payment
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MakePaymentModal;
